import React, { useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useTranslation } from 'react-i18next';
import { Box, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchCustomers } from '../../slices/customers/customersSlice';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { CustomerWithId } from '../../models/customer.model';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './CustomerProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useCustomerProfiles } from './hooks/useCustomerProfiles';
import { CustomerRow } from './components/CustomerRow';
import { CustomersTable } from './components/CustomersTable';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import { Helpers } from '../../common/helpers/helpers';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import CategoryCard from '../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../common/category-card/types';
import { ProfilesToolbar } from '../../common/profiles-list/ProfilesToolbar';
import listStyles from '../../common/profiles-list/ProfilesList.module.scss';

const CustomerProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //const [pendingStatus, setPendingStatus] = useState(true);
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);

  const {
    customers: customersCreatedByAdmin,
    setCurrentPage,
    customersCount,
    currentPage,
    getRequestedPage,
    cardView,
    setCardView,
    customersLoading,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useCustomerProfiles();

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(customersCount / itemsPerPage);
  // Shown in the actions block when there is more than one page.
  const showPagination = Boolean(customersCount && customersCount > itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  const company = useAppSelector(selectAllCompanies)[0];

  const [customerId, setCustomerId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setCustomerId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDelete = () => {
    if (customerId) {
      setAnchorEl(null);
      setDeleteMessageModalOpen(false);
      NWClient.delete('customer', customerId).then(() => {
        setCustomerId(null);
        dispatch(fetchCustomers({ company_id: company?.id ?? 0, page: currentPage }));
      });
    }
  };

  const handleEdit = (id: number) => {
    navigate(ROUTES.CUSTOMER_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocuments = (id: number) => {
    navigate(ROUTES.CUSTOMER_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleDelete = (id: number) => {
    setCustomerId(id);
    setDeleteMessageModalOpen(true);
  };

  const handleCreateCustomer = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.customer_profile;
    const count = customersCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (limit !== 'unlimited' && count >= limit) {
        setPlanMessageVisible(true);
      } else {
        navigate(ROUTES.CUSTOMER_PROFILE_CREATE);
      }
    }
  };

  /*useEffect(() => {
    if (company) {
      dispatch(fetchCustomers({ company_id: company?.id }));

      setPendingStatus(false);
    } else {
      dispatch(fetchCompanies())
        .unwrap()

        .finally(() => setPendingStatus(false));
    }
  }, [company, dispatch]);*/

  return (
    <>
      <AppHeader
        title={
          <>
            <span style={{ marginRight: '8px' }}>{t('pages.customerProfiles')}</span>
            <PreviewTooltip title={<Preview />} arrow placement='top'>
              <InfoIcon fontSize='small' className={'info-icon'} />
            </PreviewTooltip>
          </>
        }
      />
      <Grid component='div' container>
        <Box className={styles.page_description} style={{ width: '100%', marginTop: '16px' }}>
          {t('messages.customerProfilesDesription')}
        </Box>
        <Grid component='div' size={{ xs: 12 }} className={styles.customer_cards}>
          <Box
            id='customerProfilesBlock'
            className={`${listStyles.profiles_block}${
              cardView ? '' : ` ${listStyles.profiles_block_joined}`
            }`}
          >
            <ProfilesToolbar
              idPrefix='customerProfiles'
              viewLabel={t('labels.customerProfilesView')}
              searchValue={searchStringInputValue}
              setSearchValue={setSearchStringInputValue}
              searchCleared={searchCleared}
              setSearchCleared={setSearchCleared}
              resetSearchFilter={resetSearchFilter}
              showPagination={showPagination}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              paginationDisabled={customersLoading === 'loading'}
              cardView={cardView}
              setCardView={setCardView}
              addButton={
                showPagination
                  ? {
                      id: 'customerProfilesAddProfileBtn',
                      label: t('buttons.addProfile'),
                      onClick: handleCreateCustomer,
                    }
                  : undefined
              }
            />
            <Box className={listStyles.block_content}>
              {customersLoading === 'loading' && <ProgressOverlay position='absolute' />}
              {cardView ? (
                <CategoryCardGrid id='customerProfilesGrid'>
                  {[...customersCreatedByAdmin]
                    .sort((a, b) => (a.customer_name ?? '').localeCompare(b.customer_name ?? ''))
                    .map((customer: CustomerWithId) => (
                      <CategoryCard
                        id={`customerProfilesCustomersCreatedByAdminCard_${customer.id}`}
                        key={customer.id}
                        title={customer.customer_name ?? ''}
                        icon={<SupportAgentOutlinedIcon />}
                        accent={CATEGORY_CARD_ACCENTS.Customers}
                        onClick={() => navigate(ROUTES.CUSTOMER_PROFILES + '/' + customer.id)}
                        actions={
                          <Box className={styles.customer_profile_icons}>
                            <Tooltip title={t('buttons.documents')} placement='top' arrow>
                              <IconButton
                                id={`customerProfilesDocumentsIconBtn_${customer.id}`}
                                aria-label={t('buttons.documents')}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDocuments(customer.id);
                                }}
                                size='small'
                              >
                                <UploadFileOutlinedIcon
                                  id={`customerProfilesDocumentsIconBtnUploadFileIcon_${customer.id}`}
                                  fontSize='small'
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('buttons.edit')} placement='top' arrow>
                              <IconButton
                                id={`customerProfilesEditIconBtn_${customer.id}`}
                                aria-label={t('buttons.edit')}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEdit(customer.id);
                                }}
                                size='small'
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon
                                  id={`customerProfilesEditIconBtnEditIcon_${customer.id}`}
                                  fontSize='small'
                                />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              id={`customerProfilesMoreOptionsIconBtn_${customer.id}`}
                              aria-label={t('labels.menuButton')}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleClick(event, customer.id);
                              }}
                              size='small'
                              sx={{ p: 0.5 }}
                            >
                              <MoreVertIcon
                                id={`customerProfilesMoreOptionsIconBtnMoreVertIcon_${customer.id}`}
                                fontSize='small'
                              />
                            </IconButton>
                          </Box>
                        }
                      />
                    ))}
                  <Button
                    id='customerProfilesAddProfileBtn2'
                    onClick={handleCreateCustomer}
                    variant='contained'
                    color='primary'
                    className={styles.add_button}
                  >
                    <Box
                      id='customerProfilesAddProfileBtn2Box'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: '1rem',
                      }}
                    >
                      <Typography
                        id='customerProfilesAddProfileBtn2Text'
                        sx={{ fontWeight: 'light', fontSize: 14 }}
                      >
                        {t('buttons.addProfile')}
                      </Typography>
                      <AddIcon id='customerProfilesAddProfileBtn2AddIcon' />
                    </Box>
                  </Button>
                </CategoryCardGrid>
              ) : (
                <CustomersTable isEmpty={customersCreatedByAdmin.length === 0}>
                  {[...customersCreatedByAdmin].map((customer) => (
                    <CustomerRow
                      customer={customer}
                      key={customer.id}
                      handleDelete={() => handleDelete(customer.id)}
                      handleEdit={() => handleEdit(customer.id)}
                      navigateToDocs={() => handleDocuments(customer.id)}
                    />
                  ))}
                </CustomersTable>
              )}
            </Box>
          </Box>
        </Grid>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMoreVertClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            id='customerProfilesDeleteMenuItem'
            onClick={() => setDeleteMessageModalOpen(true)}
          >
            {t('buttons.delete')}
          </MenuItem>
        </Menu>

        {/*pendingStatus ? <ProgressOverlay /> : ''*/}
        {planMessageVisible && (
          <MessageModal
            open={planMessageVisible}
            modalMessage={t('messages.limitCustomers')}
            actionButtonText={t('buttons.ok')}
            actionButtonFn={() => navigate(ROUTES.PRICING)}
            goBackButtonText={t('buttons.later')}
            onClose={() => {
              setPlanMessageVisible(false);
            }}
          />
        )}
        {deleteMessageModalOpen && (
          <MessageModal
            open={deleteMessageModalOpen}
            modalTitle={t('messages.deleteCustomer')}
            modalMessage={t('messages.deleteCustomerDialog')}
            actionButtonText={t('buttons.goBack')}
            actionButtonFn={() => setDeleteMessageModalOpen(false)}
            goBackButtonText={t('buttons.delete')}
            onDismiss={() => setDeleteMessageModalOpen(false)}
            onClose={() => {
              handleProfileDelete();
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default CustomerProfiles;
